import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import ProjectSquare from "../designSystem/components/projects/ProjectSquare";
import projects from "../designSystem/data/projectsData";

// Project page components
import EchoPage from "./projects/ECHO";
import FourSeasonsFacadePage from "./projects/FourSeasonsFacade";
import CliffYouthCenterPage from "./projects/CliffYouthCenter";
import CowesWeekPavilionPage from "./projects/CowesWeekPavilion";
import SILTMiddelkerkePage from "./projects/SILTMiddelkerke";
import TheCutawayPage from "./projects/TheCutaway";
import WEAVEPage from "./projects/WEAVE";
import APEToolsPage from "./projects/ApeTools";
import DropletsClashPage from "./projects/DropletsClash";
import GENxFacadeSolutionPage from "./projects/GENxFacadeSolution";

/* ─────────────────────────────────────────────
   Map targetUrl → page component
   ───────────────────────────────────────────── */
const PAGE_MAP = {
  "/ECHO": EchoPage,
  "/FourSeasonsFacade": FourSeasonsFacadePage,
  "/CliffYouthCenter": CliffYouthCenterPage,
  "CowesWeekPavilion": CowesWeekPavilionPage,
  "/CowesWeekPavilion": CowesWeekPavilionPage,
  "/SILTMiddelkerke": SILTMiddelkerkePage,
  "/TheCutaway": TheCutawayPage,
  "/WEAVE": WEAVEPage,
  "/APETools": APEToolsPage,
  "/DropletsClash": DropletsClashPage,
  "/GENxFacadeSolution": GENxFacadeSolutionPage,
};

// Filter projects in the Computational Design category
const CD_PROJECTS = projects.filter((p) =>
  p.categories?.includes("Computational Design")
);

/* ═══════════════════════════════════════════════
   PANEL SCROLLBAR
   Mirrors the site's CustomScrollbar but scoped
   to an individual scrollable container.
   ═══════════════════════════════════════════════ */
const THUMB_H = 24;
const TRACK_GAP = 0; // no dead-zone, thumb travels full height

const PanelScrollbar = ({ containerRef, side = "right" }) => {
  const thumbRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const isDraggingRef = useRef(false);
  const dragOffsetRef = useRef(0);
  const frameRef = useRef(null);

  const syncThumb = useCallback(() => {
    const container = containerRef.current;
    if (!container || !thumbRef.current || isDraggingRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const maxScroll = scrollHeight - clientHeight;

    if (maxScroll <= 0) {
      thumbRef.current.style.opacity = "0";
      return;
    }

    thumbRef.current.style.opacity = "1";
    const trackH = clientHeight - TRACK_GAP * 2 - THUMB_H;
    const pct = Math.min(1, scrollTop / maxScroll);
    const y = TRACK_GAP + pct * trackH;
    thumbRef.current.style.transform = `translateY(${Math.round(y)}px)`;
  }, [containerRef]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    syncThumb();
    container.addEventListener("scroll", syncThumb, { passive: true });
    const ro = new ResizeObserver(syncThumb);
    ro.observe(container);

    return () => {
      container.removeEventListener("scroll", syncThumb);
      ro.disconnect();
    };
  }, [containerRef, syncThumb]);

  const onPointerDown = (e) => {
    isDraggingRef.current = true;
    setDragging(true);
    e.currentTarget.setPointerCapture?.(e.pointerId);
    document.documentElement.style.userSelect = "none";
    const rect = e.currentTarget.getBoundingClientRect();
    dragOffsetRef.current = e.clientY - rect.top;
    e.preventDefault();
  };

  useEffect(() => {
    const onMove = (e) => {
      if (!isDraggingRef.current) return;
      const container = containerRef.current;
      if (!container || !thumbRef.current) return;

      const clientY = e.clientY;

      if (!frameRef.current) {
        frameRef.current = requestAnimationFrame(() => {
          frameRef.current = null;
          const { scrollHeight, clientHeight } = container;
          const maxScroll = scrollHeight - clientHeight;
          if (maxScroll <= 0) return;

          const containerRect = container.getBoundingClientRect();
          const trackH = clientHeight - TRACK_GAP * 2 - THUMB_H;
          const desired = clientY - containerRect.top - dragOffsetRef.current;
          const constrained = Math.max(
            TRACK_GAP,
            Math.min(desired, TRACK_GAP + trackH)
          );
          const pct = (constrained - TRACK_GAP) / trackH;

          container.scrollTop = pct * maxScroll;
          thumbRef.current.style.transform = `translateY(${Math.round(constrained)}px)`;
        });
      }
      e.preventDefault();
    };

    const endDrag = () => {
      if (!isDraggingRef.current) return;
      isDraggingRef.current = false;
      setDragging(false);
      document.documentElement.style.userSelect = "";
    };

    window.addEventListener("pointermove", onMove, { passive: false });
    window.addEventListener("pointerup", endDrag);
    window.addEventListener("pointercancel", endDrag);
    window.addEventListener("blur", endDrag);

    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", endDrag);
      window.removeEventListener("pointercancel", endDrag);
      window.removeEventListener("blur", endDrag);
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
    };
  }, [containerRef]);

  return (
    <div
      ref={thumbRef}
      className="absolute top-0 w-3 h-6 bg-black dark:bg-white z-50"
      style={{
        [side]: 0,
        opacity: 0,
        touchAction: "none",
        cursor: dragging ? "grabbing" : "grab",
        transition: dragging ? "none" : "transform 75ms ease-out",
        willChange: "transform",
      }}
      onPointerDown={onPointerDown}
    />
  );
};

/* ═══════════════════════════════════════════════
   COMP DESIGN 2 — HOVER-BASED SPLIT VIEW
   Hovering a sidebar card switches the project
   content on the right. Last hovered stays selected.
   The right panel scrolls with the main page scroll;
   the left sidebar stays sticky in view.
   ═══════════════════════════════════════════════ */
const HOVER_DELAY = 150; // ms — small debounce so flicking across cards doesn't flash

const CompDesign2 = () => {
  const [activeIdx, setActiveIdx] = useState(0);
  const leftRef = useRef(null);
  const hoverTimerRef = useRef(null);

  const activeProject = CD_PROJECTS[activeIdx];
  const PageComponent = activeProject
    ? PAGE_MAP[activeProject.targetUrl]
    : null;

  // Scroll page to top when switching projects
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [activeIdx]);

  // Cleanup hover timer on unmount
  useEffect(() => {
    return () => clearTimeout(hoverTimerRef.current);
  }, []);

  const handleCardHoverEnter = (i) => {
    clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = setTimeout(() => {
      setActiveIdx(i);
    }, HOVER_DELAY);
  };

  const handleCardHoverLeave = () => {
    clearTimeout(hoverTimerRef.current);
    // Don't reset — last hovered stays selected
  };

  return (
    <div className="bg-white dark:bg-black transition-colors duration-500 min-h-screen pt-[72px]">
      {/* ── Container with side margins ── */}
      <div className="flex mx-6 lg:mx-12">

        {/* ═══ LEFT PANEL + DIVIDER — Sticky sidebar ═══ */}
        <div
          className="sticky self-start flex flex-none"
          style={{ top: '96px', height: 'calc(100vh - 120px)' }}
        >
          <div className="relative w-[180px] lg:w-[200px] xl:w-[240px]">
            <PanelScrollbar containerRef={leftRef} side="left" />

            <div
              ref={leftRef}
              className="h-full overflow-y-auto overflow-x-hidden pl-6 pr-6"
              style={{ scrollbarWidth: "none" }}
            >
              {/* Project cards */}
              <div className="flex flex-col gap-6 lg:gap-8 pr-1 pb-6 pt-2">
                {CD_PROJECTS.map((project, i) => {
                  const isActive = i === activeIdx;

                  return (
                    <div
                      key={project.title}
                      className={`
                        relative transition-transform duration-300 ease-in-out origin-center
                        ${isActive ? 'scale-[1.06] z-10' : 'scale-100 z-0'}
                      `}
                      onMouseEnter={() => handleCardHoverEnter(i)}
                      onMouseLeave={handleCardHoverLeave}
                    >
                      <ProjectSquare
                        project={project}
                        isSelected={isActive}
                        onClick={() => setActiveIdx(i)}
                        onHoverChange={() => {}}
                        disableNavigation
                        disableExpand
                        forceHovered={isActive}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="w-px bg-gray-200 dark:bg-gray-800 self-stretch" />
        </div>

        {/* ═══ RIGHT PANEL — Scrolls with page ═══ */}
        <div className="flex-1 min-w-0 pl-6 lg:pl-8 py-6">
          <AnimatePresence mode="wait">
            {PageComponent ? (
              <motion.div
                key={activeProject.targetUrl}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25, ease: "easeInOut" }}
              >
                <PageComponent />
              </motion.div>
            ) : (
              <motion.div
                key="placeholder"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex items-center justify-center min-h-[60vh]"
              >
                <p className="text-body text-gray-400">
                  Hover over a project to preview
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default CompDesign2;

